import React from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Divider,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Icon } from '@iconify/react';
import { compactFormFieldSx } from '../../../../styles/formStyles';
import { effectiveQuestionWeights, formatWeight, intraCategoryWeightTotal } from '../questionnaire.service';

const labelSx = {
    fontSize: 12,
    lineHeight: '16px',
    letterSpacing: '0.4px',
    color: '#625B71',
    textTransform: 'uppercase',
    fontWeight: 600,
    mb: 0.5,
};

const codeSx = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: 13,
    color: '#294190',
    backgroundColor: '#EEF1FA',
    borderRadius: '6px',
    px: 1,
    py: 0.25,
    display: 'inline-block',
};

/**
 * Risk values run 0 (lowest risk) to 10 (highest), so the colour has to follow the value rather
 * than the row: on this scale a high number is the bad one, which is the opposite of what a reader
 * assumes when the column is unlabelled.
 */
function riskColor(value) {
    const risk = Number(value);
    if (Number.isNaN(risk)) return '#625B71';
    if (risk <= 3) return '#4F8506';
    if (risk <= 6) return '#8A5A00';
    return '#DA342E';
}

function OptionRow({ option, editable, onChange, onRemove, canRemove }) {
    if (!editable) {
        return (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '160px minmax(0, 1fr) 90px' },
                    gap: 1.5,
                    alignItems: 'center',
                    py: 1,
                }}
            >
                <Typography sx={codeSx}>{option.optionCode}</Typography>
                <Typography sx={{ fontSize: 15, color: '#1C1B1F' }}>{option.label}</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: riskColor(option.riskValue) }}>
                    {Number(option.riskValue).toFixed(2)}
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '170px minmax(0, 1fr) 110px 44px' },
                gap: 1.5,
                alignItems: 'start',
                py: 1,
            }}
        >
            <TextField
                size="small"
                value={option.optionCode}
                onChange={(event) => onChange({ ...option, optionCode: event.target.value })}
                placeholder="OPTION_CODE"
                sx={compactFormFieldSx}
            />
            <TextField
                size="small"
                value={option.label}
                onChange={(event) => onChange({ ...option, label: event.target.value })}
                placeholder="Answer shown to the farmer"
                sx={compactFormFieldSx}
            />
            <TextField
                size="small"
                type="number"
                value={option.riskValue}
                onChange={(event) => onChange({ ...option, riskValue: event.target.value })}
                inputProps={{ min: 0, max: 10, step: 0.25 }}
                sx={compactFormFieldSx}
            />
            <Tooltip title={canRemove ? 'Remove answer' : 'A question needs at least two answers'}>
                <span>
                    <IconButton disabled={!canRemove} onClick={onRemove} sx={{ mt: 0.5 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 20, color: canRemove ? '#DA342E' : '#C4C4C4' }} />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}

function QuestionBlock({ question, editable, effectivePct, categoryWeightTotal, onChange, onRemove }) {
    const questionShare = `${formatWeight(question.intraCategoryWeight)} ÷ ${formatWeight(categoryWeightTotal)} = ${effectivePct.toFixed(1)}% of category`;

    const updateOption = (index, next) => {
        const options = [...question.options];
        options[index] = next;
        onChange({ ...question, options });
    };

    const removeOption = (index) => {
        onChange({ ...question, options: question.options.filter((_, i) => i !== index) });
    };

    const addOption = () => {
        onChange({
            ...question,
            options: [...(question.options || []), { optionCode: '', label: '', riskValue: 5 }],
        });
    };

    return (
        <Box
            sx={{
                border: '1px solid #E7ECF2',
                borderRadius: '16px',
                p: { xs: 2, sm: 2.5 },
                mb: 2,
                backgroundColor: '#FFFFFF',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    {editable ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 140px' }, gap: 1.5, mb: 1.5 }}>
                            <Box>
                                <Typography sx={labelSx}>Question code</Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    value={question.questionCode}
                                    onChange={(event) => onChange({ ...question, questionCode: event.target.value })}
                                    sx={compactFormFieldSx}
                                />
                            </Box>
                            <Box>
                                <Typography sx={labelSx}>Weight in category</Typography>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={question.intraCategoryWeight}
                                    onChange={(event) =>
                                        onChange({ ...question, intraCategoryWeight: event.target.value })
                                    }
                                    inputProps={{ min: 0, step: 0.5 }}
                                    helperText={questionShare}
                                    sx={compactFormFieldSx}
                                />
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                            <Typography sx={codeSx}>{question.questionCode}</Typography>
                            <Typography sx={{ fontSize: 13, color: '#625B71' }}>
                                Weight in category {questionShare}
                            </Typography>
                        </Box>
                    )}

                    {editable ? (
                        <>
                            <Typography sx={labelSx}>Question</Typography>
                            <TextField
                                size="small"
                                fullWidth
                                multiline
                                value={question.text}
                                onChange={(event) => onChange({ ...question, text: event.target.value })}
                                sx={compactFormFieldSx}
                            />
                        </>
                    ) : (
                        <Typography sx={{ fontSize: 16, color: '#1C1B1F', fontWeight: 500 }}>
                            {question.text}
                        </Typography>
                    )}
                </Box>

                {editable && (
                    <Tooltip title="Remove question">
                        <IconButton onClick={onRemove}>
                            <DeleteOutlineIcon sx={{ fontSize: 20, color: '#DA342E' }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            <Divider sx={{ borderColor: '#EEF2F7', mb: 1 }} />

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: editable
                        ? { xs: '1fr', sm: '170px minmax(0, 1fr) 110px 44px' }
                        : { xs: '1fr', sm: '160px minmax(0, 1fr) 90px' },
                    gap: 1.5,
                }}
            >
                <Typography sx={labelSx}>Answer code</Typography>
                <Typography sx={{ ...labelSx, display: { xs: 'none', sm: 'block' } }}>Answer</Typography>
                <Typography sx={{ ...labelSx, display: { xs: 'none', sm: 'block' } }}>Risk value</Typography>
                {editable && <Box />}
            </Box>

            {(question.options || []).map((option, index) => (
                <OptionRow
                    key={`${question.questionCode}-${index}`}
                    option={option}
                    editable={editable}
                    canRemove={(question.options || []).length > 2}
                    onChange={(next) => updateOption(index, next)}
                    onRemove={() => removeOption(index)}
                />
            ))}

            {editable && (
                <Button
                    size="small"
                    onClick={addOption}
                    startIcon={<Icon icon="tabler:plus" width="16" height="16" />}
                    sx={{ textTransform: 'none', color: '#294190', mt: 1 }}
                >
                    Add answer
                </Button>
            )}
        </Box>
    );
}

export default function CategorySection({ category, editable, effectivePct, weightTotal, onChange, onRemove }) {
    const questionWeights = effectiveQuestionWeights(category);
    const questionWeightTotal = intraCategoryWeightTotal(category);
    const categoryShare = `${formatWeight(category.globalWeightPct)} ÷ ${formatWeight(weightTotal)} = ${effectivePct.toFixed(1)}% of score`;

    const updateQuestion = (index, next) => {
        const questions = [...category.questions];
        questions[index] = next;
        onChange({ ...category, questions });
    };

    const removeQuestion = (index) => {
        onChange({ ...category, questions: category.questions.filter((_, i) => i !== index) });
    };

    const addQuestion = () => {
        onChange({
            ...category,
            questions: [
                ...(category.questions || []),
                {
                    questionCode: '',
                    text: '',
                    intraCategoryWeight: 1,
                    options: [
                        { optionCode: '', label: '', riskValue: 1 },
                        { optionCode: '', label: '', riskValue: 9 },
                    ],
                },
            ],
        });
    };

    return (
        <Accordion
            defaultExpanded={false}
            disableGutters
            sx={{
                border: '1px solid #DFE5F6',
                borderRadius: '16px !important',
                mb: 2,
                boxShadow: 'none',
                '&::before': { display: 'none' },
                overflow: 'hidden',
            }}
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#F8FAFF', px: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', width: '100%', pr: 2 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#1C1B1F' }}>
                        {category.label || category.categoryCode || 'Untitled category'}
                    </Typography>
                    <Typography sx={codeSx}>{category.categoryCode}</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography sx={{ fontSize: 14, color: '#625B71' }}>
                        {(category.questions || []).length} question{(category.questions || []).length === 1 ? '' : 's'}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#294190' }}>
                        Global weight {formatWeight(category.globalWeightPct)}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: '#625B71' }}>{categoryShare}</Typography>
                </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, py: 3, backgroundColor: '#FCFDFF' }}>
                {editable && (
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '220px minmax(0, 1fr) 180px 44px' },
                            gap: 2,
                            mb: 3,
                            alignItems: 'start',
                        }}
                    >
                        <Box>
                            <Typography sx={labelSx}>Category code</Typography>
                            <TextField
                                size="small"
                                fullWidth
                                value={category.categoryCode}
                                onChange={(event) => onChange({ ...category, categoryCode: event.target.value })}
                                sx={compactFormFieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography sx={labelSx}>Label</Typography>
                            <TextField
                                size="small"
                                fullWidth
                                value={category.label}
                                onChange={(event) => onChange({ ...category, label: event.target.value })}
                                sx={compactFormFieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography sx={labelSx}>Global weight</Typography>
                            <TextField
                                size="small"
                                fullWidth
                                type="number"
                                value={category.globalWeightPct}
                                onChange={(event) => onChange({ ...category, globalWeightPct: event.target.value })}
                                inputProps={{ min: 0, step: 5 }}
                                helperText={categoryShare}
                                sx={compactFormFieldSx}
                            />
                        </Box>
                        <Tooltip title="Remove category">
                            <IconButton onClick={onRemove} sx={{ mt: 2.5 }}>
                                <DeleteOutlineIcon sx={{ fontSize: 20, color: '#DA342E' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {category.scaleOverride?.note && (
                    <Box
                        sx={{
                            backgroundColor: '#FFF5E1',
                            border: '1px solid #F0DCB4',
                            borderRadius: '12px',
                            p: 2,
                            mb: 3,
                        }}
                    >
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#8A5A00', mb: 0.5 }}>
                            Scale override
                        </Typography>
                        <Typography sx={{ fontSize: 14, color: '#6B5322' }}>{category.scaleOverride.note}</Typography>
                    </Box>
                )}

                {(category.questions || []).map((question, index) => (
                    <QuestionBlock
                        key={`${question.questionCode}-${index}`}
                        question={question}
                        editable={editable}
                        effectivePct={questionWeights[question.questionCode] || 0}
                        categoryWeightTotal={questionWeightTotal}
                        onChange={(next) => updateQuestion(index, next)}
                        onRemove={() => removeQuestion(index)}
                    />
                ))}

                {editable && (
                    <Button
                        onClick={addQuestion}
                        startIcon={<Icon icon="tabler:plus" width="18" height="18" />}
                        sx={{ textTransform: 'none', color: '#294190', fontWeight: 600 }}
                    >
                        Add question
                    </Button>
                )}
            </AccordionDetails>
        </Accordion>
    );
}
